import { Table, Tag, Typography, Button, Space, Spin } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Header, SpaceBetween } from "@cloudscape-design/components";
import { capitalize } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router-dom";
import {
  useGetProviderAllSanctionsListQuery,
  useLazyRunProviderSanctionCheckQuery,
} from "../redux/api/provider/provider";
import { skipToken } from "@reduxjs/toolkit/query";
import { AllSanctionList } from "../redux/api/provider/types";
import IncredableDateFormat from "../common/incredable-date-format";
import { useState } from "react";

const { Link } = Typography;

const columns = [
  {
    title: "Run Date",
    key: "runDate",
    render: (_: any, record: AllSanctionList) => (
      <span>{IncredableDateFormat(record.runDate)}</span>
    ),
  },
  {
    title: "NPI Number",
    key: "npi",
    render: (_: any, record: AllSanctionList) => record.npi,
  },
  {
    title: "Sanction Result",
    key: "result",
    render: (_: any, record: AllSanctionList) =>
      record.sanctions.length > 0 ? (
        <Tag icon={<CloseCircleOutlined />} color="red">
          <b>{record.sanctions.length}</b> Sanction(s) found
        </Tag>
      ) : (
        <Tag icon={<CheckCircleOutlined />} color="green">
          No Sanctions found
        </Tag>
      ),
  },
];

const SanctionCheckScreen = () => {
  const { providerId } = useParams();
  const [triggerSanctionCheck, { isFetching, isLoading }] =
    useLazyRunProviderSanctionCheckQuery();

  const handleRunSanctionCheck = async () => {
    if (providerId) {
      await triggerSanctionCheck({ providerId });
      refetchAllSanctions();
    }
  };
  const {
    data: allsanctionData = [],
    isLoading: allSanctionDataLoading,
    isFetching: allSanctionDataFetching,
    refetch: refetchAllSanctions,
  } = useGetProviderAllSanctionsListQuery(
    providerId ? { providerId } : skipToken
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleExpandAll = () => {
    const allKeys = allsanctionData
      .filter((record) => record.sanctions.length > 0)
      .map((record) => record.id);
    setExpandedRowKeys(allKeys);
  };

  const handleCollapseAll = () => {
    setExpandedRowKeys([]);
  };

  const expandedRowRender = (record: AllSanctionList) => {
    if (record.sanctions.length === 0) return <i>No sanctions found.</i>;

    return (
      <Table
        columns={[
          {
            title: "Source",
            dataIndex: "name",
            key: "name",
            render: (name: string) => <span>{name}</span>,
          },
          {
            title: "Link",
            dataIndex: "link",
            key: "link",
            width: 180,
            render: (link: string) => (
              <Link href={link} target="_blank" rel="noopener noreferrer">
                View Source
              </Link>
            ),
          },
          {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 140,
            render: (type: string) => (
              <Tag color={type === "federal" ? "geekblue" : "gold"}>
                {capitalize(type)}
              </Tag>
            ),
          },
          {
            title: "Exclusion Date",
            dataIndex: "exclusionDate",
            width: 200,
            key: "exclusionDate",
          },
        ]}
        dataSource={record.sanctions}
        rowKey={(row) => `${row.name}-${row.exclusionDate}`}
        pagination={false}
      />
    );
  };
  return (
    <SpaceBetween size="m">
      <Header
        actions={
          <Space>
            {/* <Button
              icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
              onClick={() => { }}
            >
              Exclusion List
            </Button> */}
            <Button icon={<MinusSquareOutlined />} onClick={handleCollapseAll}>
              Collapse All
            </Button>
            <Button icon={<PlusSquareOutlined />} onClick={handleExpandAll}>
              Expand All
            </Button>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleRunSanctionCheck}
              loading={isFetching}
            >
              Run Sanction Check
            </Button>
          </Space>
        }
      >
        <Typography.Title level={3} style={{ marginTop: "0" }}>
          Sanction Monitoring Results{" "}
          <Typography.Text
            type="secondary"
            style={{ fontSize: "18px" }}
          >{`(${allsanctionData?.length})`}</Typography.Text>
        </Typography.Title>
      </Header>
      <Spin
        spinning={
          isLoading ||
          isFetching ||
          allSanctionDataFetching ||
          allSanctionDataLoading
        }
      >
        <Table
          columns={columns}
          dataSource={allsanctionData}
          rowKey={(record) => record.id}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpand: (expanded, record) => {
              setExpandedRowKeys((prev) =>
                expanded
                  ? [...prev, record.id]
                  : prev.filter((key) => key !== record.id)
              );
            },
            rowExpandable: (record: AllSanctionList) =>
              record.sanctions.length > 0,
          }}
          bordered
        />
      </Spin>
    </SpaceBetween>
  );
};

export default SanctionCheckScreen;
